/**
 * Esports domain adapter — every call this domain makes to the api goes through here, and
 * everything else in the domain imports from here rather than from @/services/api (frontend
 * ADR-002).
 *
 * One exception stands: a component may import a generated enum straight from the sdk, as
 * TeamEditor does for TeamRole, so that the values a picker offers are the ones the api
 * declares rather than a list copied into a component and left to drift.
 */
import {
  addRosterEntry,
  apiUrl,
  createSeason,
  deleteSeason,
  deleteTeam,
  fieldTeam,
  findGame,
  findGameAccounts,
  findPlayedRosters,
  findRoster,
  findSeasonContents,
  findSeasons,
  findTeamSeasons,
  findTeams,
  unfieldTeam,
  findSeasonGames,
  enterGame,
  leaveGame,
  updateSeason,
  uploadPublicImage,
} from "@/services/api"
import type {
  AddRosterEntryRequest,
  CasualGameResponse,
  FieldedTeamResponse,
  FieldTeamRequest,
  FileType,
  GameAccountResponse,
  GameRostersResponse,
  Image,
  PlayedRosterResponse,
  RosterEntryResponse,
  SeasonRequest,
  SeasonResponse,
  TeamResponse,
  TeamRole as ApiTeamRole,
  TeamRosterResponse,
} from "@/services/api"
import type {Picture} from "@/components/island/pictures"
import {accepted, refusable} from "@/domains/esports/refusals"
import {readOr} from "@/utils/answers"
import type {Saved} from "@/utils/refusals"
import type {Refused} from "@/types/api"

// Re-exported so this adapter still answers for its own surface, while the type has one definition.
export type {Refused}

/**
 * A game's code: the identity a team, a roster and a member's handle point at. A plain string
 * because the games that exist are rows rather than a list fixed when the code is built.
 */
export type GameCode = string
export type TeamRole = ApiTeamRole
export type EsportsPage = GameRostersResponse
export type Season = SeasonResponse
export type Team = TeamResponse
export type TeamRoster = TeamRosterResponse
export type RosterEntry = RosterEntryResponse
export type GameAccount = GameAccountResponse
/** A game itself: what it is called, the art it is drawn with, and how its page presents it. */
export type Game = CasualGameResponse
export type FieldedTeam = FieldedTeamResponse

/** What a season holds, so an offer to remove it can say what goes with it. */
export interface SeasonContents {
  teams: number
  players: number
}

/**
 * The images the api points at, resolved to where they are actually served.
 *
 * Done here rather than at each place one is drawn: the api answers with its own paths, and a bare
 * path resolves against the frontend's origin instead of the api's. Resolving at the one seam every
 * image comes through means no component has to remember. Every width is resolved, not only the
 * full-size one, so a component can hand the whole set to a `srcset` without checking which of them
 * are usable.
 */
const image = (one: Image): Image => ({
  ...one,
  url: apiUrl(one.url),
  renditions: one.renditions.map(rendition => ({...rendition, url: apiUrl(rendition.url)})),
})

/**
 * A picture put into storage, ready for a save to name it.
 *
 * Storing and applying are separate — the surrounding dialog is what puts the picture on a record —
 * so cancelling leaves everything as it was. What comes back is the whole image, so a picker can
 * draw it before anything is saved, and a refusal comes back in the api's own words, since
 * "something went wrong" does not tell somebody to pick another file. Owned here rather than by the
 * picker, which is shared UI taking this as a prop: the kind of picture and the endpoint that takes
 * it stay with the domain that knows both.
 */
export async function storePicture(
  file: File,
  kind: FileType,
): Promise<Saved<Picture> | Refused> {
  const stored = await refusable(uploadPublicImage({query: {type: kind}, body: {file}}), "That picture could not be stored.")
  return stored.ok ? {ok: true, saved: image(stored.saved)} : stored
}

const imageOrNone = (one?: Image | null): Image | null => (one ? image(one) : null)

const withBanner = <T extends {banner?: Image | null}>(record: T): T =>
  ({...record, banner: imageOrNone(record.banner)})

const withIcon = <T extends {icon?: Image | null}>(entry: T): T =>
  ({...entry, icon: imageOrNone(entry.icon)})

/**
 * Both of a record's pictures, resolved against the api.
 *
 * A game and a team each carry a banner and an icon, so this takes the pair. Every url an image
 * carries is resolved here and nowhere else.
 */
const withArt = <T extends {banner?: Image | null; icon?: Image | null}>(record: T): T =>
  withIcon(withBanner(record))

const withMedia = (team: TeamRoster): TeamRoster => ({
  ...withArt(team),
  members: team.members.map(withIcon),
})

export async function loadEsportsPage(game: GameCode, seasonId?: number): Promise<EsportsPage | null> {
  const res = await findGame({path: {game}, query: seasonId == null ? {} : {seasonId}})
  const page = res.data
  if (!page) return null
  return {...page, teams: page.teams.map(withMedia)}
}

export async function loadSeasons(): Promise<Season[]> {
  const res = await findSeasons()
  return res.data ?? []
}

/**
 * A season written, or the api's own account of why not.
 *
 * The body is what says the write landed; an empty answer says nothing, so it is a refusal.
 */
export async function saveSeasonOrReason(id: number | undefined, body: SeasonRequest): Promise<Saved<Season> | Refused> {
  return refusable(
    id == null ? createSeason({body}) : updateSeason({path: {id}, body}),
    "The season could not be saved.",
  )
}

export async function loadSeasonContents(id: number): Promise<SeasonContents> {
  const res = await findSeasonContents({path: {id}})
  return res.data ?? {teams: 0, players: 0}
}

/**
 * Drops a team from one season. The team, and the seasons it played, are untouched: a team
 * fielded in five seasons and dropped from one still played the other four.
 */
export async function unfieldTeamFromSeason(
  teamId: number,
  game: GameCode,
  seasonId: number,
): Promise<{ok: true} | Refused> {
  return accepted(unfieldTeam({path: {seasonId, teamId}, query: {game}}), "The team could not be dropped from the season.")
}

/** A game that ran in one season, with what it fielded. */
export interface SeasonGame {
  game: GameCode
  teams: TeamRoster[]
  /** Whether a visitor sees it. A game entered with nobody fielded is answered to the board only. */
  public: boolean
}

/**
 * Every game that ran in one season.
 *
 * One read for the whole band. What comes back depends on who is asking: the api answers a
 * visitor with the games that have a team in them, and somebody who may edit with those plus
 * the ones entered and not yet staffed. The page draws what it is given rather than deciding
 * who may see what.
 */
export async function loadSeasonGames(seasonId: number): Promise<SeasonGame[]> {
  const res = await findSeasonGames({path: {seasonId}})
  return (res.data ?? []).map(one => ({
    game: one.game,
    teams: one.teams.map(withMedia),
    public: one.public,
  }))
}

/**
 * Records that a game runs in a season, before anybody is fielded in it.
 *
 * A refusal comes back in the api's own words: a board member told only that nothing happened
 * cannot tell a game already in the season from one that may not go in at all.
 */
export async function enterGameInSeason(
  seasonId: number,
  game: GameCode,
): Promise<Saved<SeasonGame> | Refused> {
  const entered = await refusable(enterGame({path: {seasonId, game}}), "That game could not be put into the season.")
  return entered.ok ? {ok: true, saved: {game: entered.saved.game, teams: [], public: entered.saved.public}} : entered
}

/** Same reason as the others: a refusal comes back as a body rather than as a thrown error. */
export async function leaveGameInSeason(
  seasonId: number,
  game: GameCode,
): Promise<{ok: true} | Refused> {
  return accepted(leaveGame({path: {seasonId, game}}), "The game could not be taken out.")
}

export async function dropSeasonOrReason(id: number): Promise<{ok: true} | Refused> {
  return accepted(deleteSeason({path: {id}}), "The season could not be removed.")
}

/**
 * Every team the association has, not the teams of one game.
 *
 * The pool is shared: a team that has only ever played League of Legends is still one the
 * board can field in Valorant, which is the whole point of a team being the association's.
 */
export async function loadTeams(): Promise<Team[]> {
  const res = await findTeams()
  return (res.data ?? []).map(withArt)
}

export async function dropTeam(id: number): Promise<{ok: true} | Refused> {
  return accepted(deleteTeam({path: {id}}), "The team could not be removed.")
}

/**
 * The line-ups a team has, newest first: which game, in which season.
 *
 * A fielding rather than a season, because a team that played two games in one season has
 * two of them, with a line-up in each.
 */
export interface Fielding {
  game: GameCode
  season: Season
}

export async function loadTeamSeasons(teamId: number): Promise<Fielding[]> {
  const res = await findTeamSeasons({path: {teamId}})
  return res.data ?? []
}

/**
 * Fields a team in a season, optionally bringing across the line-up it last had. Naming no banner
 * leaves the art alone: a team is re-fielded to say it plays this season as often as to change its
 * picture. The body is what says the fielding happened, so an empty answer is refused, and the
 * roster writes that follow it do not land on a fielding nobody confirmed.
 */
export async function fieldTeamInSeason(
  teamId: number,
  seasonId: number,
  body: FieldTeamRequest,
): Promise<Saved<FieldedTeam> | Refused> {
  return refusable(fieldTeam({path: {seasonId, teamId}, body}), "That team could not be fielded this season.")
}

/**
 * Who played, or nothing at all where the api would not say.
 *
 * A roster that could not be read is not an empty one, and the two have to be told apart: the
 * editor writes what it holds over what is recorded, so reading emptiness into a refusal is
 * how a squad gets deleted by a failed request.
 */
export async function loadRoster(
  teamId: number,
  game: GameCode,
  seasonId: number,
): Promise<RosterEntry[] | null> {
  const roster = await readOr(findRoster({path: {teamId}, query: {game, seasonId}}), null)
  return roster?.map(withIcon) ?? null
}

export async function addToRoster(teamId: number, entry: AddRosterEntryRequest): Promise<Saved<RosterEntry> | Refused> {
  const added = await refusable(addRosterEntry({path: {teamId}, body: entry}), "That person could not be put on the roster.")
  return added.ok ? {ok: true, saved: withIcon(added.saved)} : added
}

/** Every roster spot somebody held, newest season first; nothing where the read failed. */
export async function loadPlayedRosters(userId: number): Promise<PlayedRosterResponse[]> {
  const res = await findPlayedRosters({path: {userId}})
  return res.data ?? []
}

export async function loadGameAccounts(userId: number): Promise<GameAccount[]> {
  const res = await findGameAccounts({path: {userId}})
  return res.data ?? []
}

/**
 * A member's handle for a game, written and cleared by the sdk's own calls. The editor asks them to
 * throw, so its own catch reports a refusal: a discarded one left the handle looking saved.
 */
export {clearGameAccount, setGameAccount} from "@/services/api"
